import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spinner } from '@ixicare/ui';
import { ProfileType } from '@ixicare/resources';
import connect from '../../../store/connect';
import DevicesStore from '~modules/devices/store/Devices';
import DeviceStore from '~modules/devices/store/Device';
import { t } from '~i18n';
import AuthStore from '~store/Auth';
import { FieldValues, Role } from '../components/DeviceConfigForm/schema';
import DeviceConfigForm from '../components/DeviceConfigForm/DeviceConfigForm';
import { GatingContext } from '../components/DeviceConfigForm/utils/gating';
import { State } from '~store/types';

interface Props {
  devicesStore: DevicesStore;
  deviceStore: DeviceStore;
  authStore: AuthStore;
}

const profileTypeToRole = (pt: ProfileType | null | undefined): Role => {
  switch (pt) {
    case ProfileType.WatcherrEmployee:
      return 'WATCHERR_EMPLOYEE';
    case ProfileType.Installer:
      return 'INSTALLER';
    default:
      return 'INSTALLER';
  }
};

const DeviceConfigContainer: React.FC<Props> = ({ devicesStore, deviceStore, authStore }) => {
  const { uuid } = useParams<{ uuid: string }>();

  React.useEffect(() => {
    if (!uuid) return;
    console.log(deviceStore.uuid !== uuid || !deviceStore.macAddress || !deviceStore.imeiNumber);
    if (deviceStore.uuid !== uuid || !deviceStore.macAddress || !deviceStore.imeiNumber) {
      devicesStore.reset();
      deviceStore.loadDevice(uuid);
      deviceStore.loadDeviceSettings(uuid);
    }
  }, [uuid, deviceStore, devicesStore]);

  React.useEffect(() => {
    if (!authStore.userType) return;
    if (deviceStore.uuid !== uuid || !deviceStore.deviceType) return;
    devicesStore.loadConfigSchema(authStore.userType, deviceStore.deviceType);
  }, [uuid, authStore.userType, deviceStore, deviceStore.uuid, deviceStore.deviceType, devicesStore]);

  React.useEffect(() => {
    if (devicesStore.schema) {
      deviceStore.hydrateFromSchemaMeta(devicesStore.schema.meta.device_mac, devicesStore.schema.meta.firmware);
    }
  }, [devicesStore.schema, deviceStore]);

  if (devicesStore.schemaLoading || !devicesStore.schema) {
    if (devicesStore.schemaError) {
      return (
        <Alert type="error">
          {t('device-config.error.load', {
            message: 'Could not load device configuration.',
          })}
        </Alert>
      );
    }
    return <Spinner />;
  }

  if (deviceStore.loadingSettings || deviceStore.loading || deviceStore.uuid !== uuid || !deviceStore.macAddress) {
    return <Spinner />;
  }

  const gating: GatingContext = {
    role: profileTypeToRole(authStore.userType),
    permissions: ['can_edit_device'],
  };

  const onSubmit = (payload: FieldValues) => deviceStore.saveConfig(payload);
  const isInstaller = authStore.userType === ProfileType.Installer;

  return (
    <DeviceConfigForm
      schema={devicesStore.schema}
      configValues={{ ...deviceStore.configValues, phone_number: deviceStore.phoneNumber ?? '' }}
      deviceMacs={deviceStore.macAddress ? [deviceStore.macAddress] : []}
      deviceImeis={deviceStore.imeiNumber ? [deviceStore.imeiNumber] : []}
      isInstaller={isInstaller}
      gating={gating}
      onSubmit={onSubmit}
    />
  );
};

export default connect((state: State) => ({
  devicesStore: state.devices,
  deviceStore: state.device,
  authStore: state.auth,
}))(DeviceConfigContainer);
